import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ActivationKeysService,
  StandaloneActivationKey,
} from 'src/app/services/activation-keys.service';
import { ErrorService } from 'src/app/services/error.service';
import { DesktopLicenseDetails } from 'src/app/services/licenses.service';
import { Tenant } from 'src/app/services/tenants.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-email-activation-keys',
  templateUrl: './email-activation-keys.component.html',
  styleUrl: './email-activation-keys.component.scss',
})
export class EmailActivationKeysComponent {
  @Input() tenant?: Tenant;
  @Input() licenses?: DesktopLicenseDetails[];
  @Output() emailed = new EventEmitter<void>();

  @ViewChild('sendActivationKeysDialog')
  sendActivationKeysDialog!: ElementRef<HTMLDialogElement>;

  selectedLicenses: Set<string> = new Set();
  standaloneKeys: StandaloneActivationKey[] = [];
  selectedStandaloneKeys: Set<string> = new Set();
  loadingStandaloneKeys = false;
  standaloneKeysError = '';
  private standaloneLoadId = 0;

  additionalEmails: string[] = [];
  selectedEmails: Set<string> = new Set();

  isAddingEmail = false;
  customEmail = new FormControl('', [Validators.email]);

  sendingEmail = false;
  isTransfer = false;
  errorSendingEmail = '';

  constructor(
    private activationKeysService: ActivationKeysService,
    private errorService: ErrorService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    console.log(this.activationKeysService, this.toastService);
  }

  private loadStandaloneKeys() {
    const tenantId = this.tenant?.id;
    if (!tenantId) {
      this.standaloneKeys = [];
      return;
    }

    const loadId = ++this.standaloneLoadId;
    this.loadingStandaloneKeys = true;
    this.standaloneKeysError = '';
    this.activationKeysService.listStandaloneActivationKeys(tenantId).subscribe({
      next: (res) => {
        if (loadId !== this.standaloneLoadId) {
          return;
        }
        this.loadingStandaloneKeys = false;
        this.standaloneKeys = res.standalone_activation_keys ?? [];
      },
      error: (error) => {
        if (loadId !== this.standaloneLoadId) {
          return;
        }
        this.loadingStandaloneKeys = false;
        this.standaloneKeys = [];
        this.standaloneKeysError = this.errorService.getErrorMessage(error);
      },
    });
  }

  get accountEmails() {
    return this.tenant?.emails ?? [];
  }

  get billingEmails() {
    return this.tenant?.billing_emails ?? [];
  }

  toggleTransfer() {
    this.isTransfer = !this.isTransfer;
  }

  toggleEmail(email: string) {
    if (this.selectedEmails.has(email)) {
      this.selectedEmails.delete(email);
    } else {
      this.selectedEmails.add(email);
    }
  }

  toggleLicense(licenseId: string) {
    if (this.selectedLicenses.has(licenseId)) {
      this.selectedLicenses.delete(licenseId);
    } else {
      this.selectedLicenses.add(licenseId);
    }
  }

  toggleStandaloneKey(keyId: string) {
    if (this.selectedStandaloneKeys.has(keyId)) {
      this.selectedStandaloneKeys.delete(keyId);
    } else {
      this.selectedStandaloneKeys.add(keyId);
    }
  }

  standaloneKeyLabel(key: StandaloneActivationKey) {
    return key.product_title || key.product_code;
  }

  addCustomEmail() {
    if (this.customEmail.invalid) {
      return;
    }
    if (this.customEmail.value === null) {
      return;
    }
    this.additionalEmails.push(this.customEmail.value);
    this.customEmail.reset();
    this.isAddingEmail = false;
  }

  cancelAddingEmail() {
    this.customEmail.reset();
    this.isAddingEmail = false;
  }

  openDialog() {
    // add all licenses to the selected licenses set
    if (this.licenses) {
      for (const license of this.licenses) {
        if (license.activation_key) {
          this.selectedLicenses.add(license.id);
        }
      }
    }
    this.selectedStandaloneKeys.clear();
    this.loadStandaloneKeys();
    this.sendActivationKeysDialog.nativeElement.showModal();
  }

  closeDialog() {
    this.sendActivationKeysDialog.nativeElement.onclose = () => {
      this.standaloneLoadId++;
      this.selectedLicenses.clear();
      this.selectedStandaloneKeys.clear();
      this.standaloneKeys = [];
      this.standaloneKeysError = '';
      this.selectedEmails.clear();
      this.additionalEmails = [];
      this.customEmail.reset();
      this.isAddingEmail = false;
      this.isTransfer = false;
      this.errorSendingEmail = '';
    };
    this.sendActivationKeysDialog.nativeElement.close();
  }

  emailActivationKeys() {
    if (!this.tenant) {
      this.errorSendingEmail = 'Tenant not properly initialized';
      return;
    }
    if (
      this.selectedLicenses.size === 0 &&
      this.selectedStandaloneKeys.size === 0
    ) {
      this.errorSendingEmail =
        'Please select at least one license or one-off activation key';
      return;
    }
    if (this.selectedEmails.size === 0) {
      this.errorSendingEmail = 'Please select at least one email';
      return;
    }

    this.sendingEmail = true;
    this.errorSendingEmail = '';
    console.log(
      'emailing activation keys for tenant',
      this.tenant?.id,
      'to emails',
      this.selectedEmails,
      'and licenses',
      this.selectedLicenses
    );

    this.activationKeysService
      .emailActivationKeys(
        this.tenant?.id,
        Array.from(this.selectedLicenses),
        Array.from(this.selectedStandaloneKeys),
        Array.from(this.selectedEmails),
        this.isTransfer
      )
      .subscribe({
        next: () => {
          this.sendingEmail = false;
          this.toastService.addToast(
            'success',
            'Activation keys emailed successfully'
          );
          this.emailed.emit();
          this.closeDialog();
        },
        error: (error) => {
          this.sendingEmail = false;
          this.errorSendingEmail = this.errorService.getErrorMessage(error);
          this.toastService.addToast(
            'error',
            'Failed to email activation keys'
          );
        },
      });
  }
}
